<template>
  <Transition name="guide-slide">
    <div
      v-if="modelValue"
      class="guide-panel-backdrop"
      @click.self="close"
    >
      <div
        class="guide-panel"
        ref="panelEl"
        tabindex="-1"
        @keydown.esc="close"
        role="dialog"
        aria-label="Kullanım Kılavuzu"
        aria-modal="true"
      >
        <!-- Header -->
        <div class="guide-panel-header">
          <div class="guide-panel-title">
            <span class="material-symbols-outlined guide-header-icon">menu_book</span>
            <span>Kullanım Kılavuzu</span>
          </div>
          <button class="guide-close-btn" @click="close" aria-label="Kapat" type="button">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>

        <!-- Body -->
        <div class="guide-panel-body">
          <div
            v-for="(section, idx) in sections"
            :key="idx"
            class="guide-section"
            :class="{ 'is-open': openSection === idx }"
          >
            <button
              class="guide-section-header"
              type="button"
              @click="toggleSection(idx)"
              :aria-expanded="openSection === idx"
            >
              <span class="guide-section-icon material-symbols-outlined">{{ section.icon }}</span>
              <span class="guide-section-title">{{ section.title }}</span>
              <span class="guide-section-chevron material-symbols-outlined">
                {{ openSection === idx ? 'expand_less' : 'expand_more' }}
              </span>
            </button>
            <div class="guide-section-body" v-show="openSection === idx">
              <div class="guide-section-content" v-html="section.content"></div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="guide-panel-footer">
          <span class="material-symbols-outlined guide-footer-icon">info</span>
          <span class="guide-footer-text">Yapay zeka yanıtları hata içerebilir.</span>
          <button
            type="button"
            class="guide-dismiss-btn"
            @click="dismissForever"
            title="Bu kılavuzu bir daha gösterme"
          >
            Artık Gösterme
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
export default {
  name: "GuidePanel",
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "dismiss-forever"],
  data() {
    return {
      openSection: 0,
      sections: [
        {
          icon: "smart_toy",
          title: "Sistem Nedir?",
          content: `
            <p>Mahai, Kuzey Kıbrıs Türk Cumhuriyeti (KKTC) hukuku alanında uzmanlaşmış bir yapay zeka asistanıdır.</p>
            <p>Sistem iki temel kaynak üzerinde çalışır:</p>
            <ul>
              <li><strong>Yasalar &amp; Tüzükler:</strong> KKTC mevzuat metinleri</li>
              <li><strong>Emsal Kararlar:</strong> Yüksek Mahkeme içtihatları</li>
            </ul>
            <p>Sorularınızı doğal dilde sorabilir, ilgili yasal düzenlemeleri ve emsal kararları anında keşfedebilirsiniz.</p>
          `,
        },
        {
          icon: "edit_note",
          title: "Nasıl Soru Sorulur?",
          content: `
            <p>En iyi sonuçlar için sorularınızı <strong>spesifik ve bağlamlı</strong> olarak sorun.</p>
            <div class="guide-example good">
              <div class="guide-example-label">✓ İyi örnek</div>
              <p>"İşçinin haklı nedenle iş sözleşmesini feshetmesi halinde kıdem tazminatı almaya hakkı var mı?"</p>
            </div>
            <div class="guide-example bad">
              <div class="guide-example-label">✗ Kaçınılacak</div>
              <p>"İşçi hakları nelerdir?"</p>
            </div>
            <p>Konuyu özelleştirin, zaman çerçevesi ekleyin veya madde numarasını belirtin. Bu, yapay zekanın daha isabetli sonuçlar getirmesini sağlar.</p>
          `,
        },
        {
          icon: "tips_and_updates",
          title: "Etkili Prompt İpuçları",
          content: `
            <ul>
              <li><strong>Konuyu özelleştirin:</strong> "Kira sözleşmesi" yerine "konut kiracısının tahliye sürecindeki hakları"</li>
              <li><strong>Madde numarası belirtin:</strong> Biliyorsanız "Fasıl 154 Madde 24" gibi ifadeler ekleyin.</li>
              <li><strong>Zaman çerçevesi ekleyin:</strong> "2020 sonrasındaki değişiklikler" gibi sınırlamalar sonuçları iyileştirir.</li>
              <li><strong>Bağlam verin:</strong> Sorunuzun arka planını kısaca açıklayın.</li>
              <li><strong>Takip soruları sorun:</strong> İlk yanıtı anladıktan sonra daha derin sorular sorabilirsiniz.</li>
              <li><strong>Ceza hukuku sorgularında:</strong> "hafifletici faktörler", "caydırıcı ceza", "sabıkasızlık", "tahrik" gibi terimleri doğrudan kullanın — sistem otomatik olarak ilgili emsal kararları getirir.</li>
              <li><strong>Hukuki ilkeye göre arama:</strong> "nüfuz suistimali ispat yükü hangi kararda açıklanmıştır" gibi soyut ilke sorguları da desteklenmektedir.</li>
            </ul>
          `,
        },
        {
          icon: "format_quote",
          title: "Belirli İfadeyi Taşıyan Kararı Bulma",
          content: `
            <p>Bir kararın içinde geçtiğini hatırladığınız belirli bir ifadeyi veya hukuki prensibi arıyorsanız sistem bunu özel olarak tespit eder.</p>
            <p><strong>Tırnak işareti kullanın:</strong></p>
            <div class="guide-example good">
              <div class="guide-example-label">✓ İyi örnek</div>
              <p>"suçun tafsilatında belirtilmeyen eylemden mahkûm edilemez" hangi Yargıtay kararında yer almaktadır?</p>
            </div>
            <p><strong>"Hangi kararda" kalıbını kullanın:</strong></p>
            <div class="guide-example good">
              <div class="guide-example-label">✓ İyi örnek</div>
              <p>Kamu güvenliğini ilgilendiren suçlarda caydırıcı ceza verilmesi gerektiği hangi kararda yer almaktadır?</p>
            </div>
            <div class="guide-example good">
              <div class="guide-example-label">✓ İyi örnek</div>
              <p>Sabıkasızlık ile sanığın yaşı hafifletici faktör olarak kabul edilmesi gerektiği hangi Yargıtay içtihadında belirtilmiştir?</p>
            </div>
            <p>Bu kalıplar kullanıldığında sistem önce ifadeyi birebir içeren kararları getirir, ardından konu ile ilgili diğer emsal kararları listeler.</p>
          `,
        },
        {
          icon: "folder_open",
          title: "Kaynakları Kullanma",
          content: `
            <p>Yapay zeka yanıtlarının altında <strong>"Kaynaklar"</strong> bölümü görünebilir. Bu bölüm, yanıtın dayandığı belgeleri listeler.</p>
            <ul>
              <li><strong>Dosya adına tıklayın</strong> → ilgili belge önizlemesi açılır ve alıntı yapılan kısımlar vurgulanır.</li>
              <li><strong>İndir ikonuna tıklayın</strong> → belgenin tam metnini indirin.</li>
            </ul>
            <p>Kaynakları incelemek, yapay zekanın yanıtını doğrulamak ve birincil metne ulaşmak için en güvenilir yoldur.</p>
          `,
        },
        {
          icon: "search",
          title: "Emsal Karar Arama",
          content: `
            <p>Sol menüde yer alan <strong>"Emsal Ara"</strong> özelliği, Yüksek Mahkeme kararları arasında anahtar kelime araması yapmanızı sağlar.</p>
            <ul>
              <li>Arama kutusuna konu veya taraf adı girin.</li>
              <li>Listeden ilgili kararı seçin.</li>
              <li>Seçilen karar aktif sohbete bağlam olarak eklenir.</li>
            </ul>
            <p>Bu özellik, belirli bir emsal kararı referans alarak daha odaklı sorular sormanızı kolaylaştırır.</p>
          `,
        },
        {
          icon: "warning",
          title: "Önemli Notlar",
          content: `
            <ul>
              <li><strong>Yapay zeka hata yapabilir.</strong> Yanıtlar her zaman doğrulama gerektirir; yasal karar vermek için tek başına güvenilmemelidir.</li>
              <li><strong>Yalnızca KKTC hukuku.</strong> Sistem KKTC mevzuatı ve içtihadı kapsar; Türkiye veya diğer ülke hukukuna dair kesin bilgi vermez.</li>
              <li><strong>Mevzuatı tekrar kontol ediniz.</strong> Önemli hukuki kararlarda mutlaka tekrar kontrol ediniz.</li>
              <li><strong>Güncellik.</strong> Veri tabanı periyodik olarak güncellenir; en son mevzuat değişiklikleri için resmi kaynaklara başvurun.</li>
            </ul>
          `,
        },
      ],
    };
  },
  watch: {
    modelValue(val) {
      if (val) {
        this.$nextTick(() => {
          this.$refs.panelEl?.focus();
        });
      }
    },
  },
  methods: {
    close() {
      this.$emit("update:modelValue", false);
    },
    dismissForever() {
      // Parent flag'i localStorage'a yazar ve paneli kapatır.
      this.$emit("dismiss-forever");
    },
    toggleSection(idx) {
      this.openSection = this.openSection === idx ? null : idx;
    },
  },
};
</script>

<style scoped>
/* ── CSS custom properties (self-contained) ── */
.guide-panel,
.guide-panel-backdrop {
  /* merkezi token köprüsü — _tokens.scss tek kaynak */
  --sage-primary: var(--sage);
  --sage-accent: var(--gold);
  --sage-bg-main: var(--paper);
  --sage-bg-secondary: #eef0ea;
  --sage-border: var(--rule);
  --sage-text-primary: var(--ink);
  --sage-text-secondary: var(--ink-secondary);
  --sage-text-muted: var(--ink-faint);
}

/* ── Backdrop ── */
.guide-panel-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(28, 37, 32, 0.25);
  z-index: 10001;
}

/* ── Panel ── */
.guide-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 380px;
  z-index: 10002;
  background: var(--sage-bg-main);
  border-left: 3px solid var(--sage-primary);
  display: flex;
  flex-direction: column;
  outline: none;
  box-shadow: -4px 0 24px rgba(26, 95, 74, 0.12);
}

@media (max-width: 600px) {
  .guide-panel {
    width: 100vw;
    border-left: none;
    border-top: 3px solid var(--sage-primary);
  }
}

/* ── Slide transition ── */
.guide-slide-enter-active,
.guide-slide-leave-active {
  transition: opacity 0.22s ease;
}
.guide-slide-enter-from,
.guide-slide-leave-to {
  opacity: 0;
}
.guide-slide-enter-active .guide-panel,
.guide-slide-leave-active .guide-panel {
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.guide-slide-enter-from .guide-panel,
.guide-slide-leave-to .guide-panel {
  transform: translateX(100%);
}
.guide-slide-enter-to .guide-panel,
.guide-slide-leave-from .guide-panel {
  transform: translateX(0);
}

/* ── Header ── */
.guide-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  border-bottom: 1px solid var(--sage-border);
  background: var(--sage-bg-secondary);
  flex-shrink: 0;
}

.guide-panel-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: var(--sage-primary);
  letter-spacing: 0.01em;
}

.guide-header-icon {
  font-size: 20px;
  color: var(--sage-primary);
}

.guide-close-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--sage-text-muted);
  transition: background 0.12s, color 0.12s;
}
.guide-close-btn:hover {
  background: rgba(26, 95, 74, 0.08);
  color: var(--sage-primary);
}
.guide-close-btn .material-symbols-outlined {
  font-size: 20px;
}

/* ── Body ── */
.guide-panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}

/* ── Accordion section ── */
.guide-section {
  border-bottom: 1px solid var(--sage-border);
}

.guide-section-header {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 18px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background 0.12s;
  border-left: 3px solid transparent;
}
.guide-section-header:hover {
  background: rgba(26, 95, 74, 0.04);
}
.guide-section.is-open .guide-section-header {
  border-left: 3px solid var(--sage-accent);
  background: rgba(201, 162, 39, 0.06);
}

.guide-section-icon {
  font-size: 18px;
  color: var(--sage-primary);
  flex-shrink: 0;
}
.guide-section.is-open .guide-section-icon {
  color: var(--sage-accent);
}

.guide-section-title {
  flex: 1;
  font-size: 13.5px;
  font-weight: 500;
  color: var(--sage-text-primary);
}

.guide-section-chevron {
  font-size: 18px;
  color: var(--sage-text-muted);
  flex-shrink: 0;
  transition: color 0.12s;
}
.guide-section.is-open .guide-section-chevron {
  color: var(--sage-accent);
}

/* ── Section body ── */
.guide-section-body {
  overflow: hidden;
}

.guide-section-content {
  padding: 12px 18px 16px 18px;
}

/* ── Deep selectors for v-html content ── */
.guide-section-body :deep(p) {
  margin: 0 0 8px 0;
  font-size: 13px;
  line-height: 1.6;
  color: var(--sage-text-secondary);
}
.guide-section-body :deep(p:last-child) {
  margin-bottom: 0;
}
.guide-section-body :deep(ul) {
  margin: 6px 0 8px 0;
  padding-left: 18px;
}
.guide-section-body :deep(li) {
  font-size: 13px;
  line-height: 1.6;
  color: var(--sage-text-secondary);
  margin-bottom: 4px;
}
.guide-section-body :deep(strong) {
  color: var(--sage-text-primary);
  font-weight: 600;
}

/* ── Examples ── */
.guide-section-body :deep(.guide-example) {
  border-radius: 6px;
  padding: 10px 12px;
  margin: 8px 0;
}
.guide-section-body :deep(.guide-example.good) {
  border-left: 3px solid var(--sage-primary);
  background: rgba(26, 95, 74, 0.05);
}
.guide-section-body :deep(.guide-example.bad) {
  border-left: 3px solid #c45c4a;
  background: rgba(196, 92, 74, 0.05);
}
.guide-section-body :deep(.guide-example-label) {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 4px;
  color: var(--sage-text-muted);
}
.guide-section-body :deep(.guide-example.good .guide-example-label) {
  color: var(--sage-primary);
}
.guide-section-body :deep(.guide-example.bad .guide-example-label) {
  color: #c45c4a;
}
.guide-section-body :deep(.guide-example p) {
  margin: 0;
  font-style: italic;
}

/* ── Footer ── */
.guide-panel-footer {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 18px;
  border-top: 1px solid var(--sage-border);
  background: rgba(201, 162, 39, 0.06);
  flex-shrink: 0;
  font-size: 11.5px;
  line-height: 1.5;
  color: var(--sage-text-muted);
}

.guide-footer-icon {
  font-size: 16px;
  color: var(--sage-accent);
  flex-shrink: 0;
  margin-top: 1px;
}

.guide-footer-text {
  flex: 1;
}

.guide-dismiss-btn {
  flex-shrink: 0;
  background: transparent;
  border: 1px solid var(--sage-border);
  color: var(--sage-text-secondary);
  padding: 6px 12px;
  font-size: 11.5px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.guide-dismiss-btn:hover {
  background: var(--sage-primary);
  color: #fff;
  border-color: var(--sage-primary);
}
.guide-dismiss-btn:active { transform: scale(0.97); }
</style>
